import { Divider, Stack, Typography } from '@mui/material';
import type {
  CloudSnapshotsPerVolume,
  InventoryResource,
  SnapshotLocation,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { MultiAccordionsItem } from '@/components/accordion/multiAccordionsItem';
import { dayjs } from '@/utils/dayjs';

import { OtherSnapshotsDetails } from './otherSnapshotsDetails';
import { OtherSnapshotsSummary } from './otherSnapshotsSummary';

import { SnapshotSelectionEmptyState } from '../snapshotSelection/snapshotSelectionEmptyState';

function groupSnapshotsByStartedAt(snapshots: CloudSnapshotsPerVolume[]) {
  const grouped: Record<
    string,
    { providerVolumeId: string; snapshotLocation: SnapshotLocation }[]
  > = {};

  snapshots
    .filter(
      (snap) =>
        !!snap.providerVolumeId &&
        !!snap.snapshotsData &&
        snap.snapshotsData.every((s) => !!s.snapshotLocation)
    )
    .forEach(({ providerVolumeId, snapshotsData }) => {
      snapshotsData!.forEach((snapshot) => {
        if (!snapshot.startedAt) return;

        const normalizedStartedAt = dayjs
          .utc(snapshot.startedAt)
          .startOf('minute')
          .toISOString();

        if (!grouped[normalizedStartedAt]) {
          grouped[normalizedStartedAt] = [];
        }

        grouped[normalizedStartedAt].push({
          providerVolumeId: providerVolumeId!,
          snapshotLocation: snapshot.snapshotLocation!,
        });
      });
    });

  return grouped;
}

export const AwsSnapshotList = ({
  resource,
  snapshots,
}: {
  resource: InventoryResource;
  snapshots: CloudSnapshotsPerVolume[];
}) => {
  const volumes = snapshots.map((snap) => snap.providerVolumeId);
  const numOfVols = new Set(volumes).size;
  const groupedByTime = groupSnapshotsByStartedAt(snapshots);
  const filteredTimes = Object.keys(groupedByTime).filter(
    (time) => groupedByTime[time].length === numOfVols
  );
  const [selectedTime, setSelectedTime] = useState<string>(filteredTimes[0]);
  const volLabel =
    resource.cloudProvider === Provider.Azure ? 'disk' : 'volume';

  useEffect(() => {
    setSelectedTime(filteredTimes[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshots]);

  if (!filteredTimes.length) {
    return (
      <Stack marginY='40px'>
        <SnapshotSelectionEmptyState
          description={`Couldn't find related snapshots for ${volLabel}s`}
        />
      </Stack>
    );
  }

  return (
    <Stack width='100%'>
      {filteredTimes.map((time) => {
        const timeSnapshots = groupedByTime[time];
        return (
          <div key={time}>
            <MultiAccordionsItem
              isSelected={selectedTime === time}
              onSelect={() => setSelectedTime(time)}
              isMulti={!!filteredTimes.length}
              summary={
                <OtherSnapshotsSummary
                  isMultiTimes={!!filteredTimes.length}
                  time={time}
                  numOfVols={timeSnapshots.length}
                  volLabel={volLabel}
                />
              }
              Details={
                <OtherSnapshotsDetails
                  snapshotDetails={timeSnapshots}
                  resource={resource}
                  time={time}
                >
                  <Stack gap='16px'>
                    {timeSnapshots.map((timeSnap) => {
                      const vol = timeSnap.providerVolumeId;
                      const snapId = timeSnap.snapshotLocation.snapshotId;
                      return (
                        <Stack
                          key={vol}
                          direction='row'
                          gap='24px'
                          divider={<Divider orientation='vertical' flexItem />}
                        >
                          <Typography width='170px'>{vol}</Typography>
                          <Typography>{snapId}</Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                </OtherSnapshotsDetails>
              }
            />
            <Divider />
          </div>
        );
      })}
    </Stack>
  );
};
